"use client";
import { useRovingTabs } from "@/hooks/useRovingTabs.hook";
import { WhatsAppPhone } from "@/components/marketing";
import { segmentClasses } from "@/components/marketing/industryTheme/industryTheme.util";
import { cn } from "@/lib/utils";
import type { CustomerGroup } from "../../data";

interface SegmentExplorerProps {
  items: CustomerGroup[];
  ariaLabel: string;
  tablistLabel: string;
  goalLabel: string;
  fine: string;
  chat: { initial: string; name: string; subtitle: string; time: string };
}

// The preview's `segment_explorer`: one tab per customer group, each showing
// who is in it, the goal, and the WhatsApp message that suits it. Group chip
// colours match the site's other customer-group blocks.
const SegmentExplorer = ({
  items,
  ariaLabel,
  tablistLabel,
  goalLabel,
  fine,
  chat,
}: SegmentExplorerProps) => {
  const tabs = useRovingTabs(items.length);
  const active = items[tabs.activeIndex]?.segment;

  return (
    <figure role="group" aria-label={ariaLabel} className="m-0">
      <div
        role="tablist"
        aria-label={tablistLabel}
        className="flex gap-2 overflow-x-auto px-0.5 pt-0.5 pb-2"
      >
        {items.map((item, index) => {
          const isActive = item.segment === active;
          return (
            <button
              key={item.segment}
              type="button"
              role="tab"
              id={`sg-tab-${item.segment}`}
              aria-controls={`sg-p-${item.segment}`}
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => tabs.select(index)}
              onKeyDown={(event) => tabs.onTabKeyDown(event, index)}
              className={cn(
                "shrink-0 cursor-pointer rounded-full border-[1.5px] px-[18px] py-2.5 text-[15px] font-bold whitespace-nowrap transition-colors duration-200 motion-reduce:transition-none",
                isActive
                  ? "border-primary bg-primary text-white"
                  : "border-line bg-white text-ink hover:border-line-warm",
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {items.map((item) => (
        <div
          key={item.segment}
          role="tabpanel"
          id={`sg-p-${item.segment}`}
          aria-labelledby={`sg-tab-${item.segment}`}
          hidden={item.segment !== active}
          className="mx-auto mt-6 grid max-w-[820px] items-center gap-[30px] motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 md:grid-cols-[1fr_auto]"
        >
          <div className="grid gap-2">
            <span
              className={cn(
                "justify-self-start rounded-full px-4 py-[5px] text-[14px] font-extrabold",
                segmentClasses[item.segment].chip,
              )}
            >
              {item.label}
            </span>
            <h3 className="text-[1.4rem] leading-snug font-bold tracking-[-0.01em] text-ink">
              {item.title}
            </h3>
            <p className="text-[16px] leading-relaxed text-ink">
              <b>{goalLabel}</b> {item.goal}
            </p>
            <p className="text-sm leading-normal text-ink-muted">{fine}</p>
          </div>
          <WhatsAppPhone
            chat={{ initial: chat.initial, shop: chat.name, subtitle: chat.subtitle }}
            messages={[{ text: item.message, time: chat.time }]}
          />
        </div>
      ))}
    </figure>
  );
};

export default SegmentExplorer;
